import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { track } from '@amplitude/analytics-browser';
import { goToOldAdmin } from '@tiendanube/nexo';
import nexo from 'App/nexoClient';
import { useFeatureFlag, useHasTransactions } from 'commons/hooks';
import {
  FeatureFlag,
  InstallationStatusEnum,
  KycStatusEnum,
} from 'commons/services';
import { TransactionStatusType } from 'commons/types';
import { useGetStoreConfigurations } from 'domains/Brazil/ProviderConfigurations/hooks';

export const useDashboardController = () => {
  const { t: tLanding } = useTranslation('common', {
    keyPrefix: 'landing_nuvempago',
  });
  const { push } = useHistory();
  const ABOUT_NUVEMPAGO_URL =
    'https://atendimento.nuvemshop.com.br/pt_BR/informacoes-gerais-nuvem-pago/perguntas-frequentes-sobre-o-nuvem-pago';
  const CALCULATE_ECONOMY_URL =
    'https://www.nuvemshop.com.br/ferramentas/calculadora-de-taxas';
  const handleRedirectToSettings = (
    route: string,
    target: string = '_self',
  ) => {
    track('click-redirect-to-settings');
    push(route, target);
  };
  const {
    enabled: flagStatusEnabled,
    loading: isLoadingFlag,
    error: errorFlag,
  } = useFeatureFlag(
    FeatureFlag.FS_NUVEMPAGO_NEW_PROVIDER_CONFIGURATION_ENABLED,
  );
  const {
    enabled: flagStatusEnabledNewLandingExperience,
    loading: isLoadingFlagNewLandingExperience,
    error: errorFlagNewLandingExperience,
  } = useFeatureFlag(FeatureFlag.FS_NUVEMPAGO_NEW_LANDING_EXPERIENCE);
  const {
    data: settings,
    isLoading: isLoadingSettings,
    error: errorSettings,
  } = useGetStoreConfigurations({
    refetchOnWindowFocus: false,
    cacheTime: 1000 * 60 * 5,
  });

  const completedSaleStatuses: TransactionStatusType[] = [
    'paid',
    'refunded',
    'partially_refunded',
    'chargebacked',
  ];

  const { hasTransactions, isLoading: isLoadingHasTransactions } =
    useHasTransactions({ status: completedSaleStatuses });

  const isNuvemPagoInstalled = useMemo(() => {
    if (!settings?.installationStatus) return false;
    return [
      InstallationStatusEnum.INSTALLED,
      InstallationStatusEnum.PROCESSING_INSTALLATION,
    ].includes(settings?.installationStatus);
  }, [settings]);

  const isKycBlocked = useMemo(() => {
    return settings?.status === KycStatusEnum.BLOCKED;
  }, [settings]);

  const hasAtLeastOnePaidTransaction = useMemo(() => {
    return hasTransactions;
  }, [hasTransactions]);

  const canShowNewExperience = useMemo(() => {
    if (isKycBlocked) return false;
    if (!flagStatusEnabled) return false;
    if (!flagStatusEnabledNewLandingExperience) return false;
    if (!isNuvemPagoInstalled) return false;
    if (hasAtLeastOnePaidTransaction) return false;
    return true;
  }, [
    isKycBlocked,
    flagStatusEnabled,
    isNuvemPagoInstalled,
    flagStatusEnabledNewLandingExperience,
    hasAtLeastOnePaidTransaction,
  ]);

  const isLoading = useMemo(() => {
    if (isLoadingFlag) return true;
    if (isLoadingFlagNewLandingExperience) return true;
    if (isLoadingSettings) return true;
    if (isLoadingHasTransactions) return true;
    return false;
  }, [
    isLoadingFlag,
    isLoadingSettings,
    isLoadingFlagNewLandingExperience,
    isLoadingHasTransactions,
  ]);

  const isError = useMemo(() => {
    if (errorFlag) return true;
    if (errorFlagNewLandingExperience) return true;
    if (errorSettings) return true;
    return false;
  }, [errorFlag, errorSettings, errorFlagNewLandingExperience]);

  const activate = () => {
    track('click-activate-conversion-empty-state');
    goToOldAdmin(nexo, 'payments?focus_on=nuvempago');
  };

  const openAboutPage = () => {
    track('click-CAN-conversion-empty-state');
    window.open(ABOUT_NUVEMPAGO_URL, '_blank', 'noopener,noreferrer');
  };

  return {
    canShowNewExperience,
    isLoading,
    isError,
    activate,
    openAboutPage,
    t: tLanding,
    handleRedirectToSettings,
    ABOUT_NUVEMPAGO_URL,
    CALCULATE_ECONOMY_URL,
  };
};
